import React, { useState, useEffect } from "react";
import { Checkbox, InputNumber, Select, Button, Card, Drawer } from "antd";
import { FilterOutlined, CloseOutlined } from "@ant-design/icons";
import { db } from "../../firebaseConfig";
import { collection, getDocs, query, where } from "firebase/firestore";
import "./FiltroProductos.scss";

const { Option } = Select;

const FiltroProductos = ({ inicialFilters, applyFilters }) => {
  const [categorias, setCategorias] = useState([]);
  const [subcategorias, setSubcategorias] = useState([]);
  const [allCategorias, setAllCategorias] = useState([]);
  const [search, setSearch] = useState(null);
  const [categoria, setCategoria] = useState("");
  const [subcategoria, setSubcategoria] = useState("");
  const [precioMin, setPrecioMin] = useState(null);
  const [precioMax, setPrecioMax] = useState(null);
  const [mesesSinIntereses, setMesesSinIntereses] = useState(false);
  const [tipo, setTipo] = useState("todos");
  const [diasDisponibles, setDiasDisponibles] = useState([]);
  const [pagoPor, setPagoPor] = useState("");
  
  // Estado para controlar el Drawer en móvil
  const [mobileDrawerVisible, setMobileDrawerVisible] = useState(false);

  // 1. Cargar todas las categorías de Firebase
  useEffect(() => {
    const fetchCategorias = async () => {
      try {
        const categoriasCollection = collection(db, "categorias");
        const q = query(categoriasCollection, where("habilitada", "==", true));
        const categoriasSnapshot = await getDocs(q);
        const categoriasData = categoriasSnapshot.docs.map((doc) => ({
          key: doc.id,
          ...doc.data(),
        }));
        setAllCategorias(categoriasData);
      } catch (error) {
        console.error("Error al obtener categorías:", error);
      }
    };
    fetchCategorias();
  }, []);

  // 2. 🔥 CORRECCIÓN AQUÍ: Cargar filtros iniciales Y SUBCATEGORÍAS
  useEffect(() => {
    if (inicialFilters) {
      setPrecioMin(inicialFilters.precioMin || null);
      setPrecioMax(inicialFilters.precioMax || null);
      setMesesSinIntereses(inicialFilters.mesesSinIntereses || false);
      setTipo(inicialFilters.tipo || "todos");
      setDiasDisponibles(inicialFilters.diasDisponibles || []);
      setPagoPor(inicialFilters.pagoPor || "");
      if (inicialFilters.search) setSearch(inicialFilters.search);

      // Lógica de Categoría y Subcategoría
      if (allCategorias.length > 0) {
         if (inicialFilters.categoria && inicialFilters.categoria !== "todos") {
             // Buscamos el objeto completo de la categoría para sacar sus hijos
             const catObj = allCategorias.find(c => c.key === inicialFilters.categoria);
             
             if (catObj) {
                 setCategoria(catObj.key);
                 
                 // Si tiene subcategorías, las cargamos
                 if (catObj.subcategorias) {
                     const subs = catObj.subcategorias.map((sub, index) => ({ 
                         ...sub, 
                         id: index.toString() 
                     }));
                     setSubcategorias(subs);
                 } else {
                     setSubcategorias([]);
                 }

                 // Si venía una subcategoría en el filtro la ponemos, si no, "todos"
                 setSubcategoria(inicialFilters.subcategoria || "todos");
             }
         } else {
             // Reset si es "todos"
             setCategoria("todos");
             setSubcategorias([]);
             setSubcategoria("todos");
         }
      }
    }
  }, [allCategorias, inicialFilters]);

  const handleApplyFilters = () => {
    const filters = {
      categoria,
      subcategoria,
      precioMin,
      precioMax,
      mesesSinIntereses,
      tipo,
      diasDisponibles: diasDisponibles && diasDisponibles.length > 0 ? diasDisponibles : undefined,
      pagoPor: pagoPor || undefined,
      search,
    };
    applyFilters(filters);
    setMobileDrawerVisible(false);
  };

  const handleChangeCategoria = (categoriaId) => {
    if (categoriaId === "todos") {
      setCategoria("todos");
      setSubcategoria("todos");
      setSubcategorias([]);
    } else {
      // Al cambiar categoría manual, siempre reseteamos subcategoría a "todos"
      setSubcategoria("todos"); 
      
      const categoriaSeleccionadaData = allCategorias.find(
        (cat) => cat.key === categoriaId
      );
      
      if (categoriaSeleccionadaData) {
        setCategoria(categoriaSeleccionadaData.key);
        
        if (categoriaSeleccionadaData.subcategorias) {
            const subcategoriasWithKeys = categoriaSeleccionadaData.subcategorias.map(
              (sub, index) => ({ ...sub, id: index.toString() })
            );
            setSubcategorias(subcategoriasWithKeys);
        } else {
            setSubcategorias([]);
        }
      }
    }
  };

  useEffect(() => {
    if (tipo !== "todos") {
      const categoriasFiltradas = allCategorias.filter(
        (cat) => cat.tipo === tipo
      );
      setCategorias(categoriasFiltradas);

      const categoriaActualInvalida = categoriasFiltradas.find(
        (cat) => cat.key === categoria
      );
      if (!categoriaActualInvalida && categoria !== "todos") {
        setCategoria("todos");
        setSubcategoria("todos");
      }
    } else {
      setCategorias(allCategorias);
      if (categoria === "") setCategoria("todos");
    }
  }, [allCategorias, tipo]);

  // 🔥 CONTENIDO DEL FORMULARIO
  const renderFormContent = (isMobile = false) => (
    <div className={`filters-content ${isMobile ? 'mobile-padding' : ''}`}>
      
      {/* Rango de Precios */}
      <div className="filter-section">
        <p>Rango de precio</p>
        <div className="price-range-row">
          <InputNumber
            value={precioMin}
            onChange={setPrecioMin}
            placeholder="$ Mín"
            min={0}
            className="styled-input"
          />
          <span className="range-separator">-</span>
          <InputNumber
            value={precioMax}
            onChange={setPrecioMax}
            placeholder="$ Máx"
            min={0}
            className="styled-input"
          />
        </div>
      </div>

      <div className="filter-section">
        <Checkbox
          checked={mesesSinIntereses}
          onChange={(e) => setMesesSinIntereses(e.target.checked)}
          className="styled-checkbox"
        >
          Meses sin intereses
        </Checkbox>
      </div>

      {/* Tipo */}
      <div className="filter-section">
        <p>¿Qué buscas?</p>
        <Select
          value={tipo}
          onChange={(value) => {
            setTipo(value);
            setCategoria("todos");
          }}
          style={{ width: "100%" }}
          className="styled-select"
          popupClassName="styled-dropdown"
        >
          <Option value="todos">Todo</Option>
          <Option value="producto">Productos</Option>
          <Option value="servicio">Servicios</Option>
        </Select>
      </div>

      {/* Categoría */}
      {tipo !== "todos" && (
        <div className="filter-section">
          <p>Categoría</p>
          <Select
            onChange={handleChangeCategoria}
            value={categoria}
            placeholder="Selecciona"
            style={{ width: "100%" }}
            className="styled-select"
            popupClassName="styled-dropdown"
          >
            <Option value="todos">Todas</Option>
            {categorias.map((cat) => (
              <Option key={cat.key} value={cat.key}>
                {cat.nombre}
              </Option>
            ))}
          </Select>
        </div>
      )}

      {/* Subcategoría - Se muestra si hay datos */}
      {subcategorias.length > 0 &&
        tipo !== "todos" &&
        categoria !== "todos" &&
        categoria !== "" && (
          <div className="filter-section">
            <p>Subcategoría</p>
            <Select
              value={subcategoria}
              onChange={setSubcategoria}
              placeholder="Selecciona"
              style={{ width: "100%" }}
              className="styled-select"
              popupClassName="styled-dropdown"
            >
              <Option value="todos">Todas</Option>
              {subcategorias.map((sub) => (
                <Option key={sub.id} value={sub.id}>
                  {sub.subcategoria}
                </Option>
              ))}
            </Select>
          </div>
        )}

      {/* Filtros Servicios */}
      {tipo === "servicio" && (
        <>
          <div className="filter-section">
            <p>Tipo de pago</p>
            <Select 
                value={pagoPor} 
                onChange={setPagoPor}
                style={{ width: "100%" }}
                className="styled-select"
                popupClassName="styled-dropdown"
            >
              <Option value="">Cualquiera</Option>
              <Option value="hora">Por Hora</Option>
              <Option value="dia">Por Día</Option>
              <Option value="unico">Pago Único</Option>
            </Select>
          </div>

          <div className="filter-section">
            <p>Días disponibles</p>
            <Select
              value={diasDisponibles}
              onChange={setDiasDisponibles}
              mode="multiple"
              placeholder="Selecciona días"
              maxTagCount="responsive"
              style={{ width: "100%" }}
              className="styled-select"
              popupClassName="styled-dropdown"
            >
              <Option value="lunes">Lunes</Option>
              <Option value="martes">Martes</Option>
              <Option value="miércoles">Miércoles</Option>
              <Option value="jueves">Jueves</Option>
              <Option value="viernes">Viernes</Option>
              <Option value="sábado">Sábado</Option>
              <Option value="domingo">Domingo</Option>
            </Select>
          </div>
        </>
      )}

      <Button 
        type="primary" 
        onClick={handleApplyFilters} 
        block 
        className="btn-apply"
      >
        Aplicar Filtros
      </Button>
    </div>
  );

  return (
    <div className="filtros-container-wrapper">
      <div className="filters-mobile-trigger">
        <Button 
            className="btn-trigger-filters" 
            block 
            icon={<FilterOutlined />}
            onClick={() => setMobileDrawerVisible(true)}
        >
            Filtrar búsqueda
        </Button>
      </div>

      <div className="filters-desktop">
        <Card className="filter-card">
          <h3>Filtros</h3>
          {renderFormContent(false)}
        </Card>
      </div>

      <Drawer
        title={null}
        closable={false}
        placement="bottom"
        height="85vh"
        onClose={() => setMobileDrawerVisible(false)}
        open={mobileDrawerVisible}
        className="filters-mobile-drawer"
        styles={{ body: { padding: 0 } }}
      >
        <div className="drawer-handle-bar">
            <div className="handle-pill"></div>
        </div>
        
        <div className="drawer-custom-header">
            <h3>Filtrar</h3>
            <Button 
                type="text" 
                icon={<CloseOutlined />} 
                onClick={() => setMobileDrawerVisible(false)} 
                className="close-btn"
            />
        </div>

        <div className="drawer-scrollable-content">
             {renderFormContent(true)}
        </div>
      </Drawer>
    </div>
  );
};

export default FiltroProductos;